import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { filter, switchMap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Store } from '@ngxs/store';
import { AccountState } from 'app/state/account/state';
import { Account } from 'app/core/models/account.types';
import { AccountFoldersService } from 'app/modules/folders/folders.service';
import { FileResource, Document } from 'app/shared/modules/folders/folders.types';
import { DELETE_LAST_DOCUMENT_CONFIRMATION } from 'app/shared/modules/folders/folders.constants';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { SetEthanContext } from '@state/ethan/ethan.action';
import { RelatedToType } from '@shared/enums';

@Component({
  selector: 'view-file',
  templateUrl: './view-file.component.html',
  styleUrls: ['./view-file.component.scss'],
})
export class ViewFileComponent extends OnDestroyMixin implements OnInit {
  selectedFile: FileResource;
  onDeleteAction: (document: Document, fileDeleted?: boolean) => void;
  selectedDocument: Document;
  documentVersionsDataSource: MatTableDataSource<any>;
  documentVersionsColumns: string[];
  account: Account;
  canDisplayInBrowser: boolean = false;
  showDocument: boolean = false;
  pdfUrl: SafeResourceUrl;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<ViewFileComponent>,
    private accountFoldersService: AccountFoldersService,
    private readonly cdRef: ChangeDetectorRef,
    private confirmService: ConfirmService,
    private readonly store: Store,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
  ) {
    super();
    this.documentVersionsDataSource = new MatTableDataSource();
    this.documentVersionsColumns = ['version', 'created_by', 'created_at', 'actions'];
    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  ngOnInit(): void {
    this.selectedFile = this.data.fileResource;
    this.onDeleteAction = this.data.onDeleteAction;
    this.setSelectedDocument();
    this.loadFile();
    this.store.dispatch(new SetEthanContext(this.data.fileResource, RelatedToType.FileResource));
  }

  ngOnDestroy(): void {
    this.store.dispatch(new SetEthanContext(null, null));
  }

  private loadFile(): void {
    this.accountFoldersService
      .getFile(this.selectedFile.folder_resource_id, this.selectedFile.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe((fileResource) => {
        this.documentVersionsDataSource.data = fileResource.documents.sort((a, b) => (a.id < b.id ? 1 : -1));
        this.selectedFile = fileResource;
        this.setSelectedDocument();
        this.checkFileType();
        this.cdRef.markForCheck();
      });
  }

  setSelectedDocument(document?: Document): void {
    if (document) {
      this.selectedDocument = document;
      this.checkFileType();
    } else {
      if (this.selectedFile.documents.length > 0) {
        this.selectedDocument = this.selectedFile.documents[0];
        this.checkFileType();
      }
    }
    this.updatePdfUrl();
  }

  checkFileType(): void {
    if (this.selectedDocument) {
      this.canDisplayInBrowser = this.isDisplayableInBrowser(this.selectedDocument);
    }
  }

  isDisplayableInBrowser(document: Document): boolean {
    if (!document || !document.public_name) return false;

    const publicName = document.public_name.toLowerCase();
    // Check for file extensions that can be displayed in browser
    return (
      publicName.endsWith('.pdf') ||
      publicName.match(/\.(jpe?g|png|gif|svg|bmp|webp)$/i) !== null ||
      publicName.match(/\.(html?|txt)$/i) !== null
    );
  }

  private updatePdfUrl(): void {
    if (this.selectedDocument && this.canDisplayInBrowser && this.selectedDocument.pdf_url) {
      const pageNum = this.route.snapshot.queryParams['pageNum'];
      let url = this.selectedDocument.pdf_url;

      // If it's a PDF and we have a page number, add it to the URL
      if (this.selectedDocument.public_name.toLowerCase().endsWith('.pdf') && pageNum) {
        url = url + '#page=' + pageNum;
      }

      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
  }

  downloadDocument(document: Document): void {
    window.open(document.original_url, '_blank');
  }

  deleteDocument(document: Document): void {
    const isLastDocument = (this.selectedFile.documents?.length ?? 0) <= 1;
    this.confirmService
      .openConfirmationModal(isLastDocument ? DELETE_LAST_DOCUMENT_CONFIRMATION : undefined)
      .pipe(
        untilComponentDestroyed(this),
        filter((res: boolean) => res),
        switchMap(() =>
          isLastDocument
            ? this.accountFoldersService.deleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id)
            : this.accountFoldersService.deleteDocument(
                this.selectedFile.folder_resource_id,
                this.selectedFile.id,
                document.id,
              ),
        ),
      )
      .subscribe(() => {
        if (isLastDocument) {
          this.discard();
        } else {
          this.loadFile();
        }

        if (this.onDeleteAction) {
          this.onDeleteAction(document, isLastDocument);
        }
      });
  }

  deleteFile(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((res: boolean) => res),
        switchMap(() =>
          this.accountFoldersService.deleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id),
        ),
      )
      .subscribe(() => {
        this.discard();
      });
  }

  onFileChanged(event: any): void {
    const document: File = event.target.files[0];

    this.accountFoldersService
      .uploadDocument(this.selectedFile.folder_resource_id, this.selectedFile.id, document)
      .pipe(untilComponentDestroyed(this))
      .subscribe((doc) => {
        event.target.value = '';
        this.selectedFile = doc;
        this.loadFile();
      });
  }

  discard(): void {
    this.matDialogRef.close();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
